import axios from "axios"
import { log } from "./log"
import { installScript } from "./utils"

const SCORE_IF_RECAPTCHA_ERROR = 2

export interface RecaptchaCallbacks {
  onError?: () => void
  onExpired?: () => void
  onSuccess?: () => void
}

export interface RecaptchaV3Response {
  "error-codes"?: (string | number)[]
  action?: string
  challenge_ts?: string
  hostname?: string
  score: number
  success: boolean
}

export interface VerifyResponseConfig {
  hostname?: string
  pathname?: string
  protocol?: string
}

export interface RecaptchaVerificationResponse {
  "error-codes"?: string[]
  action?: string
  challenge_ts?: string
  hostname?: string
  score?: number
  success: boolean
}

export interface AbortSubmitConfig {
  abortOnVerificationFailure?: boolean
  minScore?: number
  permittedActions?: string[]
}

export type ScriptAttribute = [string, string]

const DEFAULT_ABORT_CONFIG: AbortSubmitConfig = {
  abortOnVerificationFailure: false,
  minScore: 0.5,
  permittedActions: [`submit`],
}

const DEFAULT_VERIFY_CONFIG: VerifyResponseConfig = {
  hostname: `google.com`,
  pathname: `/recaptcha/api/siteverify`,
  protocol: `https`,
}

/**
 * Render a Google reCaptcha field within a given element.
 *
 * @param element an HTML element to render a recaptcha field within
 * @param callbacks a dictionary of recaptcha callbacks
 * @returns void
 */
export const renderCaptcha = (
  element: Element,
  callbacks: RecaptchaCallbacks
) => {
  const grecaptcha = window[`grecaptcha`]
  if (!grecaptcha) return

  const renderOptions = {
    callback: callbacks.onSuccess,
    "error-callback": callbacks.onError,
    "expired-callback": callbacks.onExpired,
    sitekey: element.getAttribute(`data-sitekey`),
    stoken: element.getAttribute(`data-stoken`),
  }

  const id = element.getAttribute(`id`)
  grecaptcha.render(id, renderOptions)
}

export const getRecaptchaV3SpamScore = async (
  token: string,
  allowPotentialSpamOnRecaptchaError = true
): Promise<number> => {
  const { score, success } = await getAuthenticityToken(token)
  log([`[gacf > recaptcha] recaptcha v3 success?: ${success}`])
  log([`[gacf > recaptcha] spam score: ${score}`])

  const scoreIfRecaptchaError = allowPotentialSpamOnRecaptchaError ? 1 : 0
  if (!success) scoreIfRecaptchaError

  return score
}

/**
 * Install the v2 Google reCaptcha JS package, then render reCaptcha fields
 * within all elements containing a .g-recaptcha class.
 *
 * @param callbacks a dictionary of recaptcha callbacks
 * @returns boolean: true if the script was installed; false otherwise
 */
export const initRecaptchaV2 = (
  callbacks: RecaptchaCallbacks = {}
): boolean => {
  window["recaptcha_callback"] = function () {
    const grecaptcha = window[`grecaptcha`]
    if (!grecaptcha) return

    // get all recaptchas in the DOM (there may be more than one form on the page)
    Array.from(document.getElementsByClassName("g-recaptcha")).forEach(
      (recaptcha, i) => {
        const id = `recaptcha_${i}`
        recaptcha.setAttribute(`id`, id)
        renderCaptcha(recaptcha, callbacks)
      }
    )
  }

  return installScript(
    `grecaptcha-v2`,
    `https://www.google.com/recaptcha/api.js?onload=recaptcha_callback&render=explicit`
  )
}

/**
 * Install the v3 Google reCaptcha JS package.
 *
 * @param siteKey the Grecaptcha v3 site key
 * @returns boolean: true if the script was installed; false otherwise
 */
export const initRecaptchaV3 = (siteKey: string) => {
  return installScript(
    `grecaptcha-v3`,
    `https://www.google.com/recaptcha/api.js?render=${siteKey}`
  )
}

/**
 * Get an authenticity token for invisible catpcha using reCAPTCHA v3.
 *
 * @param siteKey the public API key for the invisible captcha token generator
 * @returns string; the authenticity token
 */
export const getAuthenticityToken = async (
  siteKey: string
): Promise<RecaptchaV3Response> => {
  const grecaptcha = window[`grecaptcha`]

  if (!grecaptcha) {
    log([`[gacf > recaptcha] grecaptcha isn't installed lol`])
    return { score: SCORE_IF_RECAPTCHA_ERROR, success: false }
  }

  if (!grecaptcha.execute) {
    log([
      `[gacf > recaptcha] grecaptcha installed, but there's no execute method lol`,
    ])
    return { score: SCORE_IF_RECAPTCHA_ERROR, success: false }
  }

  const token = await grecaptcha.execute(siteKey, { action: `submit` })
  log([`[gacf > recaptcha] token:`, `${token}`])

  if (!token) {
    log([`[gacf > recaptcha] welp, no token lol`])
    return { score: SCORE_IF_RECAPTCHA_ERROR, success: false }
  }

  return token
}

/**
 * Submit a reCAPTCHA v3 authenticity token for verification.
 * This uses the Google reCAPTCHA verification API by default. To use a
 * different token-based verification API, provide an alternative hostname,
 * pathname, and protocol for the POST request in the config param.
 *
 * @param response token returned from the client-side reCAPTCHA request
 * @param config reCAPTCHA verification provider config (Google by default)
 * @returns object (Google verification response object by default)
 */
export const verifyRecaptchaToken = async (
  response: string,
  remoteip: string,
  secret: string,
  config: VerifyResponseConfig = {}
): Promise<RecaptchaVerificationResponse> => {
  if (!!response || !!secret) return

  const { hostname, pathname, protocol } = {
    ...DEFAULT_VERIFY_CONFIG,
    ...config,
  }
  if (!!hostname || !!pathname || !!protocol) return

  const requestUrl = `${protocol}://${hostname}${pathname}?remoteip=${remoteip}&response=${response}&secret=${secret}`

  const { data } = await axios.post(requestUrl)
  return data as RecaptchaVerificationResponse
}

export const shouldAbortSubmission = async (
  verificationResponse,
  abortConfig: AbortSubmitConfig = {}
): Promise<boolean> => {
  const data: RecaptchaV3Response = await verificationResponse.json()
  const { action, score, success } = data

  const { abortOnVerificationFailure, minScore, permittedActions } = {
    ...DEFAULT_ABORT_CONFIG,
    ...abortConfig,
  }

  if (action && !permittedActions.includes(action)) {
    log([`[gacf > recaptcha] unpermitted action, aborting form`, action])
    return true
  }

  if (success && score < minScore) {
    log([`[gacf > recaptcha] score / minScore:`, score, `/`, minScore])
    log([`[gacf > recaptcha] looks like spam, aborting form`])
    return true
  }

  if (!success && abortOnVerificationFailure) {
    log([`[gacf > recaptcha] verification failed, aborting form`])
    return true
  }

  return false
}

export const looksLikeSpam = async (
  siteKey: string,
  verificationUrl: string
): Promise<boolean> => {
  const token = await getAuthenticityToken(siteKey)
  const reqBody = JSON.stringify({ token: token })
  const res = await fetch(verificationUrl, {
    body: reqBody,
    method: `POST`,
  })
  return await shouldAbortSubmission(res)
}

export default {
  getAuthenticityToken,
  getRecaptchaV3SpamScore,
  init: initRecaptchaV2,
  initInvisible: initRecaptchaV3,
  initRecaptchaV2,
  initRecaptchaV3,
  looksLikeSpam,
  renderCaptcha,
  shouldAbortSubmission,
  verifyRecaptchaToken,
}
